import type { ProfileBackTarget } from './company-profile-route';

const ORGANIZATION_PARAM = 'organizationId';

function accessibleId(value: string, organizationIds: number[]): number | undefined {
  return organizationIds.find(id => String(id) === value);
}

/**
 * The explicit selection wins, including a stale selection that must not revive an older one.
 * A profile lens can omit the query string, so its recorded trail supplies the context then.
 */
export function profileNavigationOrganizationId(
  search: string,
  target: ProfileBackTarget | null | undefined,
  organizationIds: number[],
): number | undefined {
  const explicit = new URLSearchParams(search).get(ORGANIZATION_PARAM);
  if (explicit !== null) return accessibleId(explicit, organizationIds);
  for (let entry = target; entry; entry = entry.parent) {
    const value = new URL(entry.href, 'https://upright.invalid').searchParams.get(ORGANIZATION_PARAM);
    if (value !== null) return accessibleId(value, organizationIds);
  }
  return undefined;
}

/** Carry only organisation context, never the source page's lens, filters or result selection. */
export function withProfileOrganization(href: string, organizationId: number | undefined): string {
  const url = new URL(href, 'https://upright.invalid');
  if (organizationId === undefined) {
    url.searchParams.delete(ORGANIZATION_PARAM);
  } else {
    url.searchParams.set(ORGANIZATION_PARAM, String(organizationId));
  }
  return url.pathname + url.search + url.hash;
}

/** Revalidate persisted destinations against the current user's organisations before rendering Back. */
export function validateProfileOrganizationTrail(
  target: ProfileBackTarget,
  fallbackOrganizationId: number | undefined,
  organizationIds: number[],
): ProfileBackTarget {
  const value = new URL(target.href, 'https://upright.invalid').searchParams.get(ORGANIZATION_PARAM);
  const organizationId = value === null ? fallbackOrganizationId : accessibleId(value, organizationIds);
  return {
    ...target,
    href: withProfileOrganization(target.href, organizationId),
    parent: target.parent ? validateProfileOrganizationTrail(target.parent, organizationId, organizationIds) : null,
  };
}
